import type { CSLReferenceWithRawCitation } from '@sciflow/cite';

type CSLAuthor = NonNullable<CSLReferenceWithRawCitation['author']>[number];

const YEAR_PATTERN = /\b(1[5-9]\d{2}|20\d{2}|21\d{2})\b/;
const LEADING_BIB_MARKER_PATTERN = /^\s*\[\d+\]\s*/;

/**
 * Infers basic CSL fields from a promoted bibliography reference node string.
 *
 * Handles representative plain-text/HTML bibliography inputs, including:
 * - `Smith, J. A., Doe, A. B. 2018 Automated citation extraction from PDFs`
 * - `Smith J., Doe A., Brown T., Miller R., Johnson K., ... Anderson L. (2023) Massive collaborative research datasets`
 * - `Smith, J.A.; Doe, A.B.; Brown, T.C. (2021): Machine learning in academic publishing`
 * - `World Health Organization, UNICEF, & World Bank. (2022). Global health financing report.`
 * - `<p>This is a reference example ... CO<sub>2</sub> ...</p>`
 */
export const inferPromotedReferenceFields = (
  rawCitation: string,
): Pick<CSLReferenceWithRawCitation, 'author' | 'issued' | 'title'> => {
  const plainText = htmlToPlainText(rawCitation);
  if (!plainText) {
    return {
      author: undefined,
      issued: undefined,
      title: undefined,
    };
  }

  const yearMatch = plainText.match(YEAR_PATTERN);
  let authorText = '';
  let titleText = '';

  if (yearMatch?.index != null) {
    const yearStart = yearMatch.index;
    const year = yearMatch[0];
    authorText = trimTrailingPunctuation(plainText.slice(0, yearStart));
    titleText = trimLeadingPunctuation(plainText.slice(yearStart + year.length));
    const authors = inferAuthors(authorText);
    return {
      author: authors,
      issued: { 'date-parts': [[Number(year)]] },
      title: titleText || undefined,
    };
  }

  const sentenceBoundaryIndex = plainText.search(/[.!?]/);
  if (sentenceBoundaryIndex >= 0) {
    authorText = plainText.slice(0, sentenceBoundaryIndex + 1).trim();
    if (!isLikelyAuthorSegment(authorText)) {
      return { author: undefined, issued: undefined, title: plainText };
    }

    titleText = plainText.slice(sentenceBoundaryIndex + 1).trim();
    const authors = inferAuthors(authorText);
    return {
      author: authors,
      issued: undefined,
      title: titleText || undefined,
    };
  }

  return { author: undefined, issued: undefined, title: plainText };
};

/**
 * Converts HTML-like bibliography content into a normalized single-line text.
 */
export const htmlToPlainText = (value: string): string => {
  if (!value || typeof value !== 'string') {
    return '';
  }

  const sanitizedHtml = value.replace(/<(script|style|noscript)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ');

  if (typeof document !== 'undefined' && typeof document.createElement === 'function') {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = sanitizedHtml;
    wrapper.querySelectorAll('script,style,noscript').forEach((node) => node.remove());
    return normalizeWhitespace(wrapper.textContent || '');
  }

  return normalizeWhitespace(sanitizedHtml.replace(/<[^>]*>/g, ' '));
};

/**
 * Infers a CSL author object from a plain text author segment.
 */
export const inferAuthor = (value: string): CSLAuthor | undefined => {
  const cleaned = value.replace(LEADING_BIB_MARKER_PATTERN, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) {
    return undefined;
  }

  if (cleaned.includes(',')) {
    const [family, ...rest] = cleaned.split(',');
    const normalizedFamily = family?.trim();
    const given = rest.join(',').trim();
    if (normalizedFamily && given) {
      return { family: normalizedFamily, given } as CSLAuthor;
    }
    if (normalizedFamily) {
      return { literal: normalizedFamily } as CSLAuthor;
    }
  }

  const tokens = cleaned.split(' ').filter(Boolean);
  if (tokens.length >= 2) {
    const isCorporateSegment = cleaned.includes('&');
    if (!isCorporateSegment) {
      return { literal: cleaned } as CSLAuthor;
    }
    const family = tokens[tokens.length - 1];
    const given = tokens.slice(0, -1).join(' ');
    return { family, given } as CSLAuthor;
  }

  return { literal: cleaned } as CSLAuthor;
};

/**
 * Infers one or more CSL author objects from a plain text author segment.
 *
 * Strategy by format:
 * - `A; B; C`: split by semicolon and parse each segment via `inferAuthor`.
 * - `Family, Given` style lists (including `and` / `&`): parse comma-based person pairs.
 * - `Family Initial., Family Initial., ... Family Initial.`: parse compact token lists.
 * - Comma-separated organization lists without person-pair patterns: return literal authors.
 * - Fallback: parse as a single author segment via `inferAuthor`.
 */
export const inferAuthors = (value: string): CSLAuthor[] | undefined => {
  const cleaned = value.replace(LEADING_BIB_MARKER_PATTERN, '').replace(/\s+/g, ' ').trim();
  if (!cleaned) {
    return undefined;
  }

  // Strongest delimiter for multiple authors.
  if (cleaned.includes(';')) {
    const authors = cleaned
      .split(';')
      .map((segment) => inferAuthor(segment))
      .filter((author): author is CSLAuthor => Boolean(author));
    return authors.length > 0 ? authors : undefined;
  }

  // In common plain-text citation strings, person names are usually "Family, Given".
  // If commas are present, split conjunctions like "and"/"&" as author separators.
  if (cleaned.includes(',')) {
    const commaNormalized = cleaned.replace(/\s*,?\s*(and|&)\s+/gi, ', ');
    const rawTokens = commaNormalized
      .split(',')
      .map((token) => normalizeAuthorListToken(token))
      .filter(Boolean);

    const looksLikeCompactFamilyGivenList =
      rawTokens.length >= 2 && rawTokens.every((token) => looksLikeFamilyGivenToken(token));
    const shouldParseAsLiteralList =
      rawTokens.length >= 2 && !looksLikePersonCommaPairs(rawTokens);
    const canParseAsFamilyGivenPairs = rawTokens.length >= 4 && rawTokens.length % 2 === 0;

    if (looksLikeCompactFamilyGivenList) {
      const authors = rawTokens
        .map((token) => parseFamilyGivenToken(token))
        .filter((author): author is CSLAuthor => Boolean(author));
      return authors.length > 0 ? authors : undefined;
    } else if (shouldParseAsLiteralList) {
      return rawTokens
        .map((token) => trimAuthorLiteralToken(token))
        .filter(Boolean)
        .map((literal) => ({ literal } as CSLAuthor));
    } else if (canParseAsFamilyGivenPairs) {
      const authors: CSLAuthor[] = [];
      for (let i = 0; i < rawTokens.length; i += 2) {
        const author = inferAuthor(`${rawTokens[i]}, ${rawTokens[i + 1]}`);
        if (author) {
          authors.push(author);
        }
      }
      if (authors.length > 1) {
        return authors;
      }
    }

    const conjunctionParts = cleaned
      .replace(/,\s*(and|&)\s+/gi, ' || ')
      .split(' || ')
      .map((segment) => segment.trim())
      .filter(Boolean);

    if (conjunctionParts.length > 1) {
      const authors = conjunctionParts
        .map((segment) => inferAuthor(segment))
        .filter((author): author is CSLAuthor => Boolean(author));
      if (authors.length > 0) {
        return authors;
      }
    }

  }

  const singleAuthor = inferAuthor(cleaned);
  return singleAuthor ? [singleAuthor] : undefined;
};

/**
 * Normalizes spacing and trims text.
 */
export const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

/**
 * Trims common punctuation prefixes from a text segment.
 */
export const trimLeadingPunctuation = (value: string): string =>
  value.replace(/^[\s)\].,;:\-]+/, '').trim();

/**
 * Trims common punctuation suffixes from a text segment.
 */
export const trimTrailingPunctuation = (value: string): string =>
  value.replace(/[\s(\[,;:\-]+$/, '').trim();

/**
 * Determines whether a pre-sentence segment resembles an author name.
 */
const isLikelyAuthorSegment = (value: string): boolean => {
  const cleaned = value.trim();
  if (!cleaned) {
    return false;
  }

  if (cleaned.includes(',')) {
    return true;
  }

  const tokens = cleaned.split(/\s+/).filter(Boolean);
  if (tokens.length < 2 || tokens.length > 4) {
    return false;
  }

  return tokens.every((token) => /^([A-Z][\p{L}\-']*|[A-Z]\.)$/u.test(token));
};

/**
 * Trims trailing punctuation from organization/literal author tokens.
 */
const trimAuthorLiteralToken = (value: string): string => value.replace(/[.,;:\s]+$/, '').trim();

/**
 * Normalizes per-author tokens in comma-separated lists, including leading ellipsis forms.
 */
const normalizeAuthorListToken = (value: string): string => value.replace(/^\s*\.{3,}\s*/, '').trim();

/**
 * Detects compact person tokens like `Smith J.` or `Smith J.A.`.
 */
const looksLikeFamilyGivenToken = (value: string): boolean => {
  const tokens = value.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) {
    return false;
  }
  const givenToken = tokens[tokens.length - 1];
  return /^([A-Z]\.)+$/.test(givenToken);
};

/**
 * Parses compact person tokens (`Family GivenInitials`) into CSL family/given fields.
 */
const parseFamilyGivenToken = (value: string): CSLAuthor | undefined => {
  const tokens = value.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) {
    return undefined;
  }
  const family = tokens[0];
  const given = tokens.slice(1).join(' ');
  if (!family || !given) {
    return undefined;
  }
  return { family, given } as CSLAuthor;
};

const looksLikePersonCommaPairs = (tokens: string[]): boolean => {
  if (tokens.length < 2 || tokens.length % 2 !== 0) {
    return false;
  }

  for (let index = 1; index < tokens.length; index += 2) {
    const givenToken = tokens[index];
    if (!givenToken || /^[A-Z]{2,}$/.test(givenToken)) {
      return false;
    }
  }

  return true;
};
